import React, { useMemo, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import {
  Collapse,
  useCollapseState,
  ValidatedBootstrapPanel,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { URLOptionsSection, MobileUrlSection } from '@bingads-webui-campaign-react/validated-url-input';
import { getAdvancedSettingsStyles } from './advanced-settings-styles';

export const AdvancedSettings = ({
  mobileFinalUrls,
  trackingUrlTemplate,
  finalUrlSuffix,
  urlCustomParameters,
  triggerTestUurl,
}) => {
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions, Token } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const classes = useDefaultStyles(getAdvancedSettingsStyles);

  const [
    allAdvancedSettingsSections,
    advancedSettingsExpandedSections,
    setAdvancedSettingsExpandedSections,
  ] = useCollapseState(
    ['mobile-final-url', 'advanced-url-options'] /* all sections */,
    [] /* initially expanded sections */
  );

  const allAdvancedSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdvancedSettingsExpandedSections }),
    [setAdvancedSettingsExpandedSections]
  );

  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const advancedInputSections = () => (
    <Collapse allPanelItems={allAdvancedSettingsSections} checkItems skipItemsTypeCheck activeKeys={advancedSettingsExpandedSections} setActiveKeys={setAdvancedSettingsExpandedSections}>
      <MobileUrlSection
        key="mobile-final-url"
        expand={allAdvancedSettingsExpand}
        permissions={permissions}
        mobileFinalUrls={mobileFinalUrls}
      />
      <URLOptionsSection
        key="advanced-url-options"
        expand={allAdvancedSettingsExpand}
        trackingUrlTemplate={trackingUrlTemplate}
        finalUrlSuffix={finalUrlSuffix}
        urlCustomParameters={urlCustomParameters}
        token={Token}
        triggerTestUurl={triggerTestUurl}
      />
    </Collapse>
  );

  return (
    <>
      <ValidatedBootstrapPanel
        onToggle={onAdvancedSettingsSectionToggle}
        expanded={advancedSettingsSectionExpanded}
        actuationProps={{
          expand: allAdvancedSettingsExpand,

        }}
        className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
        render={() => (
          <>
            <BootstrapPanel.Heading>
              <BootstrapPanel.Title toggle>
                {i18n.getString(_TL_('Advanced settings'))}
              </BootstrapPanel.Title>
            </BootstrapPanel.Heading>
            <BootstrapPanel.Collapse>
              <BootstrapPanel.Body>
                <div className={classNames('advanced-settings-section')}>
                  {advancedInputSections()}
                </div>
              </BootstrapPanel.Body>
            </BootstrapPanel.Collapse>
          </>
          )}
      />
    </>
  );
};

AdvancedSettings.propTypes = {
  finalUrlSuffix: PropTypes.string,
  mobileFinalUrls: PropTypes.string,
  trackingUrlTemplate: PropTypes.string,
  urlCustomParameters: PropTypes.arrayOf(PropTypes.shape({})),
  triggerTestUurl: PropTypes.func.isRequired,
};

AdvancedSettings.defaultProps = {
  finalUrlSuffix: undefined,
  mobileFinalUrls: undefined,
  trackingUrlTemplate: undefined,
  urlCustomParameters: undefined,
};
